/**
 * Data Center Editor Page — интерактивный 2D-редактор ЦОДа
 *
 * Слои (снизу вверх):
 *   GridLayer → TraysLayer → RacksLayer → LensLayer → OverlayLayer
 *
 * Инструменты: Select (V), Draw Tray (D), Erase (E)
 * Горячие клавиши: Ctrl+Z / Ctrl+Y, Ctrl+A, Ctrl+D, Ctrl+S, Delete, Escape
 * Режимы: Черновик (auto-save) → Публикация (атомарно обновляет rack_configs)
 */

import { useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { DataCenterCanvas } from '../components/editor/DataCenterCanvas'
import { EditorToolbar } from '../components/editor/EditorToolbar'
import { ObjectPalette } from '../components/editor/ObjectPalette'
import { useEditorStore } from '../store/editorStore'
import { useDataCenterEditor } from '../hooks/useDataCenterEditor'
import { DraftsSidebar } from '../components/editor/DraftsSidebar'
import { PropertiesSidebar } from '../components/editor/PropertiesSidebar'
import { Server, CheckSquare, AlertCircle, MousePointer2, Route, Eraser } from 'lucide-react'
// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DataCenterEditorPage() {
  const params = useParams<{ dcCode: string }>()
  const code = params.dcCode ?? ''
  const stageRef = useRef<any>(null)
  
  const {
    dc, dcQuery, drafts, templates, currentDraftId, setCurrentDraftId,
    handleSave, handlePublish, handleDeleteDraft, handleApplyTemplate,
    handleExportJson, handleExportPng
  } = useDataCenterEditor(code, stageRef)

  const { saveStatus } = useEditorStore()

  useEffect(() => {
    document.title = dc ? `${dc.name} — План зала | DC Cable Link` : 'План зала | DC Cable Link'
  }, [dc])

  if (!dc) {
    const msg = dcQuery.isError
      ? `Ошибка: ${(dcQuery.error as any)?.response?.status === 404 ? 'ЦОД не найден' : (dcQuery.error as any)?.message || 'API недоступен'}`
      : 'Загрузка плана зала...';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', flexDirection: 'column', gap: '12px' }}>
        <div>{msg}</div>
        {dcQuery.isError && (
          <a href={`/calculator/${code}`} style={{ color: '#06b6d4', fontSize: '13px' }}>
            → Открыть калькулятор для {code}
          </a>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-app, #0b0e14)', overflow: 'hidden' }}>
      {/* Toolbar */}
      <EditorToolbar
        dcName={dc.name}
        dcCode={code}
        draftName={drafts.find(d => d.id === currentDraftId)?.name}
        onExportJson={handleExportJson}
        onExportPng={handleExportPng}
        onSave={handleSave}
        onPublish={handlePublish}
        canPublish={!!currentDraftId}
        isSaving={saveStatus === 'saving'}
        stageRef={stageRef}
      />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left sidebar */}
        <aside style={{
          width: 200,
          flexShrink: 0,
          background: 'var(--bg-panel, #0d1117)',
          borderRight: '1px solid var(--border, #1a2744)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <ObjectPalette />
          <div style={{ height: 1, background: '#1a2744', margin: '4px 0' }} />
          <DraftsSidebar
            drafts={drafts}
            currentDraftId={currentDraftId}
            onSelect={setCurrentDraftId}
            onDelete={handleDeleteDraft}
            templates={templates}
            onApply={handleApplyTemplate}
          />
        </aside>

        {/* Main canvas */}
        <main style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <DataCenterCanvas
            dcCode={code}
            stageRef={stageRef}
          />
        </main>

        {/* Right sidebar */}
        <aside style={{
          width: 220,
          flexShrink: 0,
          background: 'var(--bg-panel, #0d1117)',
          borderLeft: '1px solid var(--border, #1a2744)',
          overflowY: 'auto',
        }}>
          <PropertiesSidebar dc={dc} dcCode={code} />
        </aside>
      </div>

      {/* Status bar */}
      <div style={{
        height: 24,
        background: 'var(--bg-deep, #060a0f)',
        borderTop: '1px solid var(--border, #1a2744)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: 16,
        fontSize: 11,
        color: 'var(--text-muted, #4b5563)',
      }}>
        <StatusBar />
      </div>
    </div>
  )
}

function StatusBar() {
  const { tool, selectedIds, racks } = useEditorStore()
  const canUndo = useEditorStore(s => s._historyIdx > 0)
  const dirtyCount = Object.values(racks).filter(r => r.isDirty).length
  const totalRacks = Object.keys(racks).length

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Total objects */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary, #9ca3af)' }}>
          <Server size={11} style={{ color: 'var(--color-accent-blue, #60a5fa)' }} />
          Объектов: <strong style={{ fontFamily: 'var(--font-data)', color: 'var(--text-primary, #f3f4f6)', fontWeight: 600 }}>{totalRacks}</strong>
        </span>

        {/* Selected */}
        {selectedIds.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-accent-blue, #60a5fa)' }}>
            <CheckSquare size={11} />
            Выделено: <strong style={{ fontFamily: 'var(--font-data)', fontWeight: 600 }}>{selectedIds.length}</strong>
          </span>
        )}

        {/* Dirty count */}
        {dirtyCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--status-warning, #f59e0b)' }}>
            <AlertCircle size={11} />
            Изменено: <strong style={{ fontFamily: 'var(--font-data)', fontWeight: 600 }}>{dirtyCount}</strong>
          </span>
        )}

        {/* Active tool badge */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted, #6b7280)', borderLeft: '1px solid var(--border, #1a2744)', paddingLeft: 10 }}>
          {tool === 'select' && <MousePointer2 size={11} />}
          {tool === 'draw_tray' && <Route size={11} />}
          {tool === 'erase' && <Eraser size={11} />}
          Инструмент: <span style={{ color: 'var(--text-secondary, #9ca3af)', fontWeight: 500 }}>{tool === 'select' ? 'Выбор (V)' : tool === 'draw_tray' ? 'Трасса (D)' : 'Стереть (E)'}</span>
        </span>
      </div>

      {/* Shortcuts bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted, #6b7280)', fontSize: 10 }}>
        <span style={{ opacity: canUndo ? 1 : 0.5 }}>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+Z</kbd> Отменить
        </span>
        <span>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+C</kbd> / <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+V</kbd> Буфер
        </span>
        <span>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Ctrl+D</kbd> Дубликат
        </span>
        <span>
          <kbd style={{ background: 'var(--bg-input, #1f2937)', border: '1px solid var(--border, #374151)', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-data)', color: 'var(--text-secondary)' }}>Del</kbd> Удалить
        </span>
      </div>
    </div>
  )
}
